import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/components/auth/AuthProvider";
import { usePreferences } from "@/hooks/usePreferences";
import { Button } from "@/components/ui/primitives";
import { ROUTES } from "@/constants/routes";
import { useNotificationSlot } from "@/components/system/NotificationCoordinator";

const STORAGE_KEY = "cookie_consent";

// Routes where the banner should NOT mount. Auth forms have a bottom
// CTA on the same Y axis as the banner; covering the submit button
// hurts conversion and breaks Playwright auth tests (the banner
// intercepts pointer events on the submit button). Users will see
// the banner on Frontpage before they ever reach login/signup, so
// suppressing it here costs nothing.
const SUPPRESS_ROUTES = new Set<string>(["/login", "/signup"]);
type ConsentValue = "all" | "essential";

/**
 * Cookie consent banner.
 *
 * Bottom sheet on first visit. Two-choice (accept all / essential only)
 * is the simplest GDPR-valid pattern: no pre-ticked boxes, equal weight
 * to both options. Persists choice in localStorage so we never nag
 * twice on the same device.
 *
 * If the user is signed in when they make a choice, we also mirror the
 * decision to user_preferences.analytics_opt_in so the backend can stop
 * recording analytics_events for them server-side. Mirror is best-effort
 * — a failed update doesn't block the user.
 */
export function CookieConsentBanner() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { updatePreferences } = usePreferences();
  const location = useLocation();
  const [wantsToShow, setWantsToShow] = useState(false);

  useEffect(() => {
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (!existing) setWantsToShow(true);
    } catch {
      // localStorage can throw in private mode / SSR — fail open and
      // show the banner. Worse case: user dismisses it again next visit.
      setWantsToShow(true);
    }
  }, []);

  const suppressed = SUPPRESS_ROUTES.has(location.pathname);
  const visible = useNotificationSlot("cookie", wantsToShow && !suppressed);

  const handleChoice = (value: ConsentValue) => {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {
      // Ignore — we still hide the banner for this session.
    }
    if (user) {
      // Best-effort sync to server-side preferences. We don't await or
      // surface errors: the localStorage write is the source of truth
      // for the consent gate.
      try {
        updatePreferences.mutate({ analytics_opt_in: value === "all" });
      } catch {
        // no-op
      }
    }
    setWantsToShow(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-live="polite"
      aria-label={t("legal.cookie.title")}
      className="fixed bottom-20 md:bottom-4 right-4 left-4 sm:left-auto z-50 max-w-sm animate-fade-in"
    >
      <div className="rounded-2xl border border-line/40 bg-background/95 backdrop-blur-xl shadow-lg p-4">
        <h2 className="font-semibold text-text mb-1 text-sm">{t("legal.cookie.title")}</h2>
        <p className="text-xs text-muted mb-3 leading-relaxed">
          {t("legal.cookie.body")}{" "}
          <Link to={ROUTES.PRIVACY} className="tap-target text-brand underline font-medium">
            {t("legal.cookie.privacy_link")}
          </Link>
        </p>
        {/* Stacked below 380px: the two labels side by side are wider
            than a 320px screen. */}
        <div className="grid grid-cols-1 min-[380px]:grid-cols-2 gap-2">
          <Button
            variant="secondary"
            className="flex-1 text-sm px-3 py-3"
            onClick={() => handleChoice("essential")}
          >
            {t("legal.cookie.essential_only")}
          </Button>
          <Button
            variant="primary"
            className="flex-1 text-sm px-3 py-3"
            onClick={() => handleChoice("all")}
          >
            {t("legal.cookie.accept_all")}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CookieConsentBanner;
